import currency from 'currency.js';
import { Store } from '@betgames/bg-state-manager';
import { GameId } from '@bg-shared/enums/GameId';
import { StoresEntity } from '@bg-shared/business/StoreFeature';
import { optionIDConverter } from '@bg-shared/business/CasinoBetting/utils/optionIDConverter';
import { partnerSettingsStore } from '@bg-shared/business/PartnerSettings';
import { balanceStore } from '@bg-shared/business/Balance';
import { stagedBetsStore } from '@bg-shared/business/StagedBets';
import { ChipActionType } from '../enums';
import { IAmountAction, IChipAction, ISectorChoiceAction, IChipAmounts } from '../interfaces';

export const casinoBetAmountsEntity = new StoresEntity(
    class CasinoBetAmounts extends Store<IChipAmounts> {
        // key -> "optionItemID" generated with "optionIDConverter"
        constructor(private gameId: GameId) {
            super({
                key: `CasinoBetAmounts/${gameId}`,
                default: {},
            });
        }

        public clearAmounts(ids: string[]): void {
            this.update((draft) => {
                ids.forEach((id) => {
                    delete draft[id];
                });
            });
        }

        public updateFromActions(actions: IChipAction[][]): void {
            let newAmounts: IChipAmounts = {};

            actions.forEach((sequence) => {
                const action = sequence[0];

                if (action.pendingUndo) {
                    return;
                }

                switch (action.type) {
                    case ChipActionType.ADD: {
                        action.optionItemIDs.forEach((optionItemID, index) => {
                            newAmounts[optionItemID] = currency(newAmounts[optionItemID] || 0).add(
                                action.amounts[index],
                            ).value;

                            // cleanup not fully selected items for this betting option
                            const { oddId } = optionIDConverter.parse(optionItemID);

                            Object.keys(newAmounts).forEach((optionID) => {
                                if (optionIDConverter.hasOptionId(optionID, oddId.toString())) {
                                    const { items } = optionIDConverter.parse(optionID);

                                    if (items?.filter((item) => item === null).length === 1) {
                                        delete newAmounts[optionID];
                                    }
                                }
                            });
                        });

                        break;
                    }
                    case ChipActionType.SECTOR_CHOICE: {
                        sequence.forEach((item) => {
                            const sequenceItem = item as ISectorChoiceAction;

                            const existingOptionKey = Object.keys(newAmounts).find((key) =>
                                optionIDConverter.hasOptionId(key, sequenceItem.optionItemIDs[0]),
                            );

                            if (existingOptionKey) {
                                const { oddId, items } = optionIDConverter.parse(existingOptionKey);

                                items[sequenceItem.index] = sequenceItem.item;

                                const newKey = optionIDConverter.create(oddId, items);

                                newAmounts[newKey] = newAmounts[existingOptionKey];

                                if (newKey !== existingOptionKey) {
                                    delete newAmounts[existingOptionKey];
                                }
                            } else {
                                const items = Array.from(
                                    { length: sequenceItem.itemsCount },
                                    (_, i) => (i === sequenceItem.index ? sequenceItem.item : null),
                                );

                                newAmounts[
                                    optionIDConverter.create(
                                        Number(sequenceItem.optionItemIDs[0]),
                                        items,
                                    )
                                ] = 0;
                            }
                        });

                        break;
                    }
                    case ChipActionType.MOVE: {
                        const { diffAmount } = action;

                        newAmounts[action.optionItemIDs[0]] = currency(
                            newAmounts[action.optionItemIDs[0]] || 0,
                        ).add(diffAmount || newAmounts[action.moveFromOptionItemID]).value;

                        delete newAmounts[action.moveFromOptionItemID];

                        break;
                    }
                    case ChipActionType.DOUBLE: {
                        Object.keys(newAmounts).forEach((key) => {
                            const diffAmount = action.diffAmount?.[key];
                            const doubleAmount = diffAmount ?? newAmounts[key];

                            newAmounts[key] = currency(newAmounts[key]).add(doubleAmount).value;
                        });

                        break;
                    }
                    case ChipActionType.REMOVE: {
                        delete newAmounts[action.optionItemIDs[0]];

                        break;
                    }
                    case ChipActionType.REPEAT: {
                        // Clear bets before repeat
                        newAmounts = {};

                        (sequence as IAmountAction[]).forEach((repeatAction) => {
                            [newAmounts[repeatAction.optionItemIDs[0]]] = repeatAction.amounts;
                        });

                        break;
                    }
                    default: {
                        break;
                    }
                }
            });

            this.update(newAmounts);

            if (partnerSettingsStore.isRealtimeBalanceEnabled) {
                const draftAmount = Object.values(this.value).reduce(
                    (total, value) => total + value,
                    0,
                );

                balanceStore.updateStagedBalance(this.gameId, draftAmount);
            }

            if (partnerSettingsStore.isUnitedKingdomMarket) {
                stagedBetsStore.updateFromCasinoChips(this.gameId, this.value);
            }
        }
    },
);
